/**
 * Column Helper Utilities for DataTable
 *
 * These helpers make it easier to create standardized column definitions
 * for common column types. They reduce boilerplate and ensure consistency.
 *
 * Usage Examples:
 *
 * 1. Text Column with Filter & Sort:
 *    createTextColumn('name', 'common.name', { showSortInFilter: true })
 *
 * 2. Date Range Column:
 *    createDateRangeColumn('createdDate', 'common.created_date', {
 *      dateFormat: 'DD MMM YYYY',
 *      showSortInFilter: true
 *    })
 *
 * 3. Select Column:
 *    createSelectColumn('status', 'common.status', STATUS_OPTIONS, {
 *      showSortInFilter: true
 *    })
 */

import moment from 'moment';
import { FilterMatchMode } from 'primereact/api';
import {
  textFilterTemplate,
  dateFilterTemplate,
  dateRangeFilterTemplate,
  dateRangeFilterFunction,
  singleSelectFilterTemplate,
  multiSelectFilterTemplate,
} from './filterComponents';

/**
 * Creates a text column with filtering and optional sorting
 * @param {string} field - The field name in the data
 * @param {string} label - The translation key for the column header
 * @param {Object} options - Additional configuration
 * @param {boolean} options.showSortInFilter - Show sort options in filter menu
 * @param {Function} options.body - Custom body renderer
 * @param {string} options.headerTooltip - Tooltip translation key
 * @param {string} options.exportField - Field to use for export
 * @returns {Object} Column configuration object
 */
export const createTextColumn = (field, label, options = {}) => {
  return {
    label,
    value: field,
    sortable: false,
    filter: true,
    showSortInFilter: options.showSortInFilter ?? false,
    filterMatchMode: FilterMatchMode.CONTAINS,
    filterElement: textFilterTemplate,
    ...(options.body && { body: options.body }),
    ...(options.headerTooltip && { headerTooltip: options.headerTooltip }),
    ...(options.exportField && { exportField: options.exportField }),
  };
};

/**
 * Creates a date column with single date filtering
 * @param {string} field - The field name in the data
 * @param {string} label - The translation key for the column header
 * @param {Object} options - Additional configuration
 * @param {string} options.dateFormat - Display format (default: 'DD MMM YYYY')
 * @param {string} options.sourceFormat - Source data format (default: 'YYYY-MM-DD hh:mm:ss')
 * @param {boolean} options.showSortInFilter - Show sort options in filter menu
 * @param {string} options.headerTooltip - Tooltip translation key
 * @returns {Object} Column configuration object
 */
export const createDateColumn = (field, label, options = {}) => {
  const dateFormat = options.dateFormat || 'DD MMM YYYY';
  const sourceFormat = options.sourceFormat || 'YYYY-MM-DD hh:mm:ss';

  return {
    label,
    value: field,
    body: (rowData) =>
      rowData?.[field] && rowData?.[field] !== '-'
        ? moment(rowData[field], sourceFormat).format(dateFormat)
        : '-',
    sortable: false,
    filter: true,
    showSortInFilter: options.showSortInFilter ?? false,
    isDateColumn: true,
    filterMatchMode: FilterMatchMode.CUSTOM,
    filterElement: dateFilterTemplate,
    filterFunction: (value, filter) => {
      if (!filter) {
        return true;
      }
      if (!value || value === '-') {
        return false;
      }
      const date = moment(value, sourceFormat);
      const filterDate = moment(filter);
      return date.isSame(filterDate, 'day');
    },
    ...(options.headerTooltip && { headerTooltip: options.headerTooltip }),
  };
};

/**
 * Creates a date range column with start and end date filtering
 * @param {string} field - The field name in the data
 * @param {string} label - The translation key for the column header
 * @param {Object} options - Additional configuration
 * @param {string} options.dateFormat - Display format (default: 'DD MMM YYYY')
 * @param {string} options.sourceFormat - Source data format (default: 'YYYY-MM-DD hh:mm:ss')
 * @param {boolean} options.showSortInFilter - Show sort options in filter menu
 * @param {string} options.headerTooltip - Tooltip translation key
 * @returns {Object} Column configuration object
 */
export const createDateRangeColumn = (field, label, options = {}) => {
  const dateFormat = options.dateFormat || 'DD MMM YYYY';
  const sourceFormat = options.sourceFormat || 'YYYY-MM-DD hh:mm:ss';

  return {
    label,
    value: field,
    body: (rowData) =>
      rowData?.[field] && rowData?.[field] !== '-'
        ? moment(rowData[field], sourceFormat).format(dateFormat)
        : '-',
    sortable: false,
    filter: true,
    showSortInFilter: options.showSortInFilter ?? false,
    isDateColumn: true,
    filterMatchMode: FilterMatchMode.CUSTOM,
    filterElement: dateRangeFilterTemplate,
    filterFunction: (value, filter) =>
      dateRangeFilterFunction(value, filter, sourceFormat),
    ...(options.headerTooltip && { headerTooltip: options.headerTooltip }),
  };
};

/**
 * Creates a select/dropdown column with filtering
 * @param {string} field - The field name in the data
 * @param {string} label - The translation key for the column header
 * @param {Array} selectOptions - Array of {label, value} options
 * @param {Object} options - Additional configuration
 * @param {boolean} options.showSortInFilter - Show sort options in filter menu
 * @param {Function} options.body - Custom body renderer
 * @param {string} options.headerTooltip - Tooltip translation key
 * @param {FilterMatchMode} options.filterMatchMode - Filter match mode (default: EQUALS)
 * @param {Function} options.filterFunction - Custom filter function
 * @returns {Object} Column configuration object
 */
export const createSelectColumn = (
  field,
  label,
  selectOptions,
  options = {},
) => {
  return {
    label,
    value: field,
    sortable: false,
    filter: true,
    showSortInFilter: options.showSortInFilter ?? false,
    filterMatchMode: options.filterMatchMode || FilterMatchMode.EQUALS,
    filterElement: (filterOptions) =>
      singleSelectFilterTemplate(filterOptions, selectOptions),
    ...(options.body && { body: options.body }),
    ...(options.filterFunction && {
      filterFunction: options.filterFunction,
    }),
    ...(options.headerTooltip && { headerTooltip: options.headerTooltip }),
  };
};

/**
 * Creates a multi-select/dropdown column with filtering for multiple values
 * @param {string} field - The field name in the data
 * @param {string} label - The translation key for the column header
 * @param {Array} selectOptions - Array of {label, value} options
 * @param {Object} options - Additional configuration
 * @param {boolean} options.showSortInFilter - Show sort options in filter menu
 * @param {Function} options.body - Custom body renderer
 * @param {string} options.headerTooltip - Tooltip translation key
 * @param {Function} options.itemTemplate - Custom template for dropdown items
 * @returns {Object} Column configuration object
 */
export const createMultiSelectColumn = (
  field,
  label,
  selectOptions,
  options = {},
) => {
  return {
    label,
    value: field,
    sortable: false,
    filter: true,
    showSortInFilter: options.showSortInFilter ?? false,
    filterMatchMode: FilterMatchMode.IN,
    filterElement: (filterOptions) =>
      multiSelectFilterTemplate(
        filterOptions,
        selectOptions,
        options.itemTemplate,
      ),
    ...(options.body && { body: options.body }),
    ...(options.headerTooltip && { headerTooltip: options.headerTooltip }),
  };
};

/**
 * Creates a custom column with full control
 * @param {string} field - The field name in the data
 * @param {string} label - The translation key for the column header
 * @param {Object} config - Full column configuration
 * @returns {Object} Column configuration object
 */
export const createCustomColumn = (field, label, config = {}) => {
  return {
    label,
    value: field,
    sortable: false,
    filter: false,
    ...config,
  };
};

/**
 * Clears a specific column filter value
 * @param {Object} prevFilters - Current filters state
 * @param {string} field - The field to clear
 * @param {number|null} constraintIndex - Index of constraint to clear (null for entire field)
 * @returns {Object} Updated filters object
 */
export const clearColumnFilter = (prevFilters, field, constraintIndex) => {
  const newFilters = { ...prevFilters };

  if (
    constraintIndex !== null &&
    Array.isArray(newFilters[field]?.constraints)
  ) {
    // Clear specific constraint
    newFilters[field] = {
      ...newFilters[field],
      constraints: newFilters[field].constraints.map((c, idx) =>
        idx === constraintIndex ? { ...c, value: null } : c,
      ),
    };
  } else if (newFilters[field]) {
    // Clear entire field filter
    if (Array.isArray(newFilters[field]?.constraints)) {
      newFilters[field] = {
        ...newFilters[field],
        constraints: newFilters[field].constraints.map((c) => ({
          ...c,
          value: null,
        })),
      };
    } else {
      newFilters[field] = {
        ...newFilters[field],
        value: null,
      };
    }
  }

  return newFilters;
};

/**
 * Clears all column filter values (except global)
 * @param {Object} prevFilters - Current filters state
 * @returns {Object} Updated filters object
 */
export const clearAllColumnFilters = (prevFilters) => {
  const newFilters = { ...prevFilters };

  // Clear all filter values except global
  Object.keys(newFilters).forEach((field) => {
    if (field === 'global') {
      return;
    }

    if (Array.isArray(newFilters[field]?.constraints)) {
      newFilters[field] = {
        ...newFilters[field],
        constraints: newFilters[field].constraints.map((c) => ({
          ...c,
          value: null,
        })),
      };
    } else if (newFilters[field]) {
      newFilters[field] = {
        ...newFilters[field],
        value: null,
      };
    }
  });

  return newFilters;
};

/**
 * Example: Create all columns for a typical table
 *
 * const columns = [
 *   createTextColumn('name', 'common.name', {
 *     showSortInFilter: true,
 *     headerTooltip: 'tooltips.name'
 *   }),
 *   createTextColumn('email', 'common.email', {
 *     showSortInFilter: true
 *   }),
 *   createDateRangeColumn('createdDate', 'common.created_date', {
 *     showSortInFilter: true
 *   }),
 *   createSelectColumn('status', 'common.status', [
 *     { label: 'Active', value: 'ACTIVE' },
 *     { label: 'Inactive', value: 'INACTIVE' }
 *   ], {
 *     showSortInFilter: true,
 *     body: (row) => <Badge>{row.status}</Badge>
 *   })
 * ];
 */
